import React from 'react';
import {
    Col,
    FormGroup,
    Card,
    CardBody,
    CardTitle,
} from 'reactstrap';
import { isEmpty } from 'lodash-es';
import { LogLevel, HubConnectionBuilder } from '@microsoft/signalr';
import { faUserClock } from '@fortawesome/free-solid-svg-icons';
import i18n from '@s20/i18n';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, SupportedFileExtensions } from '../Common';
import {
    FormLabel,
    FlexCenterRow,
    toasty,
    FormCheckbox,
    FlexColumnCenter,
    FlexRow,
    onReactSelectChanged,
    Textarea,
} from '../common/forms/FormElements';
import SlideForm from '../common/forms/SlideForm';
import { YesNoButtons } from '../common/forms/YesNoButtons';
import { BaseFormViewModel } from '../common/ViewModel';
import { handleFormSaveError } from '../common/forms/ValidationError';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import SignatureButton from '../common/forms/SignatureButton';
import SignatureImage from '../common/forms/SignatureImage';
import SurveyQuestion, { onTextAnswerChanged, onSelectAnswerChanged, onSelectChanged } from '../formsAndSurveys/SurveyQuestion';
import FileUpload from '../common/forms/FileUpload';
import { JobTypes } from '../job/Job';
import { SaveButton } from '../common/forms/SaveButton';
import { SignatureReviewTable } from './SignatureReviewTable';
import { NoReasonInput } from './ClientTimesheetSignature';
import { TimesheetSignatureType } from './ClientReview';
import WorkCardValidationStatus from './WorkCardValidationStatus';
import './ClientReview.scss';

const WorkCardTypes = {
    RequiredAll: 1,
    RequiredCrewLead: 4,
    Optional: 2,
    NotApplicable: 3,
};

const WORK_CARD_VALIDATION_STATUS_VALID = 3; // WorkCardValidationState.Valid

const toWorkCardValidationStatus = (data) => ({
    status: data.validationStatusName,
    statusId: data.validationStatusId,
    failureReasonId: data.failureReasonId,
    failureReasonMessage: data.failureReasonMessage,
    originalBlurScore: data.originalBlurScore,
    originalBrightnessScore: data.originalBrightnessScore,
    originalContrastScore: data.originalContrastScore,
    blurScore: data.blurScore,
    brightnessScore: data.brightnessScore,
    ocrConfidence: data.ocrConfidence,
    enhancementAttempted: data.enhancementAttempted,
    wasEnhanced: data.wasEnhanced,
    enhancementsApplied: data.enhancementsApplied,
    issueTypes: data.issueTypes || [],
    isRecoverable: data.isRecoverable,
    processingStep: data.processingStep,
});

export default class EmployeeReview extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            reviews: [],
            employees: [],
            formValidated: false,
            isOpen: false,
            signatureTypes: [],
            selectedEmployeeId: null,
            selectedReview: {},
            timesheetId: null,
            displayReuse: false,
            hasAgreedToDriverDisclaimerError: false,
            hasAgreedToDriveCrew: false,
            jobTypesOptions: [],
            workCardUploading: false,
            workCardValidationStatus: null,
            workCardHubConnection: null,
            earlyDepartureSelections: {}, // { [employeeId]: { leftEarly: false, categoryId: null } }
            earlyDepartureLeftEarlyOptions: [
                { value: false, label: 'No' },
                { value: true, label: 'Yes' },
            ],
            earlyDepartureCategoryOptions: [
                { value: 9, label: '1-15 Minutes' },
                { value: 10, label: '16-30 Minutes' },
                { value: 11, label: '31+ Minutes' },
            ],
            ...new BaseFormViewModel(),
        };
        this.baseState = stateBase;
        this.state = stateBase;
        this.resetForm = this.resetForm.bind(this);

        this.onReviewTextAnswerChanged = this.onReviewTextAnswerChanged.bind(this);
        this.onReviewSelectAnswerChanged = this.onReviewSelectAnswerChanged.bind(this);
        this.onReviewSelectChanged = this.onReviewSelectChanged.bind(this);
        this.onJobTypeChanged = this.onJobTypeChanged.bind(this);
    }

    componentWillUnmount() {
        this.disconnectWorkCardSignalR();
    }

    isCrewDriver = () => {
        const { selectedReview } = this.state;
        const relevantEquipmentTypes = ['truck', 'van', 'tma'];

        const { timesheetDetails } = selectedReview.timeSheet;

        // Check for a match in equipmentType.description and assigned employee
        return timesheetDetails.some((detail) => {
            const { jobAssignment, equipment } = detail;

            // Validate equipmentType and description
            const matchesEquipmentType = equipment?.equipmentType?.description
                ? relevantEquipmentTypes.some((type) =>
                    equipment.equipmentType.description.toLowerCase().includes(type),
                )
                : false;

            // Validate that the job assignment is for the selected employee
            const matchesAssignedEmployee =
                jobAssignment?.equipmentEmployeeAssignmentId === selectedReview.employeeId;

            // Return true if both conditions are met
            return matchesEquipmentType && matchesAssignedEmployee;
        });
    };

    hasAgreedToDriverDisclaimer = () => {
        const { selectedReview } = this.state;
        const { timesheetDetails } = selectedReview.timeSheet;

        return timesheetDetails.some((detail) => detail.employeeId === selectedReview.employeeId && detail.jobAssignment.hasAgreedToDriverDisclaimer);
    };

    shouldShowProjectCompletion = () => {
        const { selectedReview } = this.state;
        return !!selectedReview?.requireProjectCompletionReview && !!selectedReview?.isCrewLead;
    };

    onChangeNotes = (e) => {
        const { selectedReview } = this.state;
        selectedReview.notes = e.target.value;
        this.setState({ selectedReview });
    };

    onClear = () => {
        this.eSigPad.clear();
    };

    onClose = () => {
        this.disconnectWorkCardSignalR();
        this.resetForm();
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        this.props.onClose();
    };

    onEmployeeSelected = (employee) => {
        if (this.state.selectedEmployeeId === employee.value) {
            return; // Avoid unnecessary state update
        }

        const { reviews, timesheetId } = this.state;
        const selectedReview = reviews.find((review) => review.employeeId === employee.value);

        this.setState((prevState) => ({
            selectedReview,
            selectedEmployeeId: employee.value,
            earlyDepartureSelections: {
                ...prevState.earlyDepartureSelections,
                [employee.value]: { leftEarly: false, categoryId: null },
            },
        }));
        void this.getWorkCard(timesheetId, employee.value);

        if (selectedReview?.isComplete || selectedReview?.reviewSignature?.sigIsComplete) {
            void this.getReviewSignatureImage(selectedReview.id);
        }
    };

    onMissingSigReasonChanged = (reason) => {
        const { selectedReview } = { ...this.state };
        selectedReview.missingSigReasonId = reason.value;
        this.setState({ selectedReview });
    };

    onReuseSignatureChanged = (e) => {
        const include = e.target.checked;
        const { selectedReview } = { ...this.state };
        selectedReview.reuseEmployeeSignature = include;
        this.setState({ selectedReview });

        if (include && !selectedReview.reviewSignature?.signatureData) {
            void this.getReviewSignatureImage(selectedReview.id);
        }
    };

    onReviewSelectAnswerChanged = onSelectAnswerChanged;

    onReviewSelectChanged = onSelectChanged;

    // #region Questions/Answers
    onReviewTextAnswerChanged = onTextAnswerChanged;

    handleUploadStart = () => {
        const { tenantSettings } = this.props;
        const { timesheetId } = this.state;
        const validating = Boolean(tenantSettings?.enableWorkCardValidation);

        this.setState({
            workCardUploading: true,
            workCardValidationStatus: validating ? { status: 'pending' } : null,
        });

        if (validating) {
            this.setupWorkCardSignalR(timesheetId);
        }
    };

    handleUploadComplete = async (result) => {
        const { selectedEmployeeId, timesheetId } = this.state;
        const { tenantSettings } = this.props;

        await this.getWorkCard(timesheetId, selectedEmployeeId);

        this.setState({
            workCardUploading: false,
            workCardValidationStatus:
                tenantSettings?.enableWorkCardValidation && result ? toWorkCardValidationStatus(result) : null,
        });
    };

    getWorkCard = async (timesheetId, selectedEmployeeId) => {
        try {
            const workCardDetails = await httpClient.js(
                ApiRoutes.timesheetReview.getEmployeeWorkCard(timesheetId, selectedEmployeeId),
            );

            this.setState((prevState) => ({
                selectedReview: {
                    ...prevState.selectedReview,
                    workCard: workCardDetails?.imagePath,
                },
            }));
        } catch (error) {
            console.error('Error fetching work card:', error);
            // Don't block the UI, but log the error for debugging
        }
    };

    getReviewSignatureImage = async (reviewId) => {
        try {
            const result = await httpClient.js(ApiRoutes.timesheetReview.signatureImage(reviewId));

            this.setState((prevState) => ({
                selectedReview: {
                    ...prevState.selectedReview,
                    reviewSignature: {
                        ...prevState.selectedReview.reviewSignature,
                        signatureData: result?.data ? `data:image/png;base64,${result.data}` : null,
                    },
                },
            }));
        } catch (error) {
            console.error('Error fetching signature image:', error);
            // Don't block the UI, but log the error for debugging
        }
    };

    setupWorkCardSignalR = (timesheetId) => {
        if (this.state.workCardHubConnection) {
            return; // Already connected
        }

        const hubConnection = new HubConnectionBuilder()
            .withUrl('workCardHub')
            .configureLogging(LogLevel.Error)
            .withAutomaticReconnect()
            .build();

        const groupName = `Timesheet-${timesheetId}`;

        hubConnection.on('WorkCardValidationUpdated', (json) => {
            try {
                const data = JSON.parse(json);
                const { selectedEmployeeId } = this.state;

                // Only update if this is for the currently selected employee
                if (data.employeeId === selectedEmployeeId) {
                    this.setState({
                        workCardValidationStatus: toWorkCardValidationStatus(data),
                        workCardUploading: false,
                    });

                    // If validation passed, refresh the work card image
                    if (data.validationStatusId === WORK_CARD_VALIDATION_STATUS_VALID) {
                        this.getWorkCard(data.timesheetId, data.employeeId);
                    }
                }
            } catch (err) {
                console.error('Error parsing work card validation update:', err);
            }
        });

        hubConnection.onreconnected(() => {
            hubConnection.invoke('JoinGroup', groupName).catch(() => { });
        });

        hubConnection
            .start()
            .then(() => {
                hubConnection.invoke('JoinGroup', groupName);
                this.setState({ workCardHubConnection: hubConnection });
            })
            .catch((err) => console.error('WorkCard SignalR connection failed:', err));
    };

    disconnectWorkCardSignalR = () => {
        const { workCardHubConnection, timesheetId } = this.state;
        if (workCardHubConnection) {
            const groupName = `Timesheet-${timesheetId}`;
            workCardHubConnection.invoke('LeaveGroup', groupName).catch(() => { });
            workCardHubConnection.stop().catch(() => { });
            this.setState({ workCardHubConnection: null });
        }
    };

    shouldShowWorkCard = () => {
        const { tenantSettings } = this.props;
        const { selectedReview } = this.state;

        // First check if tenant has work cards enabled
        if (!tenantSettings.showWorkCardOnTimesheet) {
            return false;
        }

        // Then check contract setting exists
        if (!selectedReview?.workCardTypeId) {
            return false;
        }

        // Check if it's not set to N/A
        return parseInt(selectedReview.workCardTypeId) !== WorkCardTypes.NotApplicable;
    };

    isWorkCardRequired = () => {
        const { selectedReview } = this.state;

        if (selectedReview?.timeSheet?.isEmergency) {
            return false;
        }

        return parseInt(selectedReview?.workCardTypeId) === WorkCardTypes.RequiredAll
            || (parseInt(selectedReview?.workCardTypeId) === WorkCardTypes.RequiredCrewLead && selectedReview.isCrewLead);
    };

    isWorkCardValidating = () => {
        const { tenantSettings } = this.props;

        // If validation is disabled, never block the save button
        if (!tenantSettings?.enableWorkCardValidation) return false;

        const { workCardUploading, workCardValidationStatus } = this.state;
        if (workCardUploading) return true;
        const status = workCardValidationStatus?.status;
        return status === 'pending' || status === 'processing';
    };

    onSave = async () => {
        const { selectedReview, timesheetId } = { ...this.state };
        const { tenantSettings } = { ...this.props };

        const errors = [];

        this.setState({ saving: true });

        // Check if disclaimer is required
        if (tenantSettings.enableDriverRideConfirmationFields && this.isCrewDriver()) {
            // Synchronize hasAgreedToDriverDisclaimer
            if (this.hasAgreedToDriverDisclaimer()) {
                selectedReview.hasAgreedToDriverDisclaimer = this.hasAgreedToDriverDisclaimer();
            }
            // selectedReview.hasAgreedToDriverDisclaimer = this.hasAgreedToDriverDisclaimer();

            // Validation for "Driver Disclaimer"
            if (!selectedReview.hasAgreedToDriverDisclaimer) {
                this.setState({ hasAgreedToDriverDisclaimerError: true });
                errors.push(i18n.t('confirm:driverDisclaimerMustAgree'));
            }
        }

        if (selectedReview.signatureTypeId !== TimesheetSignatureType.EmployeeNoSignature) {
            if (!((selectedReview.reviewSignature.signatureData
                // Make sure it's not just the mime type prefix.
                && selectedReview.reviewSignature.signatureData !== 'data:image/png;base64,') && (selectedReview.newEmployeeSignature || selectedReview.reuseEmployeeSignature))
            ) {
                errors.push(i18n.t('timesheet:signatureRequired'));
            }
        } else {
            // no signature notes or reason is required.
            if (!tenantSettings.enablePredefinedTimesheetMissingSigReason && !selectedReview.notes) {
                errors.push(i18n.t('timesheet:noSignatureReasonRequired'));
            }
            if (tenantSettings.enablePredefinedTimesheetMissingSigReason && !selectedReview.missingSigReasonId) {
                errors.push(i18n.t('timesheet:noSignatureReasonSelectionRequired'));
            }
        }

        let requiredQuestionsAnswered = true;

        if (selectedReview.questions.length) {
            if (selectedReview.questions.some((q) => q.answerRequired)) {
                const requiredQuestions = selectedReview.questions.filter((q) => q.answerRequired);

                requiredQuestions.forEach((rq) => {
                    const ans = selectedReview.answers.filter((x) => x.questionId === rq.id) ?? [];

                    if (!ans.length) {
                        requiredQuestionsAnswered = false;
                    }
                });
            }
        }

        if (!!selectedReview.chargeTypeQuestionGroups?.length ?? false) {
            const ctqs = selectedReview.chargeTypeQuestionGroups.flatMap((ctqg) => ctqg.chargeTypeQuestions);

            if (ctqs.some((q) => q.answerRequired)) {
                const requiredCTQs = ctqs.filter((q) => q.answerRequired);

                requiredCTQs.forEach((rq) => {
                    const ans = selectedReview.chargeTypeAnswers.filter((a) => a.questionId === rq.id) ?? [];

                    if (!ans.length) {
                        requiredQuestionsAnswered = false;
                    }
                });
            }
        }

        if (this.shouldShowWorkCard() && this.isWorkCardRequired()
            && (selectedReview.workCard === null || selectedReview.workCard === undefined)) {
            errors.push(i18n.t('timesheet:workCardRequired'));
        }

        if (selectedReview.isCrewLead && selectedReview?.jobTypeId !== JobTypes.Historic
            && (selectedReview.jobTypeId === null || selectedReview.jobTypeId === undefined)) {
            errors.push(i18n.t('timesheet:jobTypeSelectionRequired'));
        }

        if (selectedReview.timeSheet.timesheetDetails.some(
            (tsd) =>
                selectedReview.requiredDetailNotes.some((x) => x.timesheetDetailId === tsd.id)
                && !tsd.notes
                && selectedReview.employeeId === tsd.employeeId,
        )) {
            errors.push(i18n.t('timesheet:explanationRequired'));
        }

        if (!requiredQuestionsAnswered) {
            errors.push(i18n.t('timesheet:requiredQuestionsMissing'));
        }

        const { earlyDepartureSelections } = this.state;
        const earlyDeptSelection = earlyDepartureSelections[selectedReview.employeeId] ?? { leftEarly: false, categoryId: null };
        const showEarlyDeparture = tenantSettings.exemptLateArrivalsFromBumpRules && selectedReview?.isUnionWork;

        if (showEarlyDeparture && earlyDeptSelection.leftEarly && !earlyDeptSelection.categoryId) {
            errors.push(i18n.t('timesheet:earlyDepartureDurationRequired'));
        }

        if (errors.length) {
            const errorsstring = errors.join(' ');
            toasty.error(errorsstring);
            this.setState({ formValidated: true, saving: false });
            return;
        }

        try {
            const payload = selectedReview;

            // Nullify jobAssignment and equipment in timesheetDetails
            payload.timeSheet.timesheetDetails = payload.timeSheet.timesheetDetails.map((detail) => ({
                ...detail,
                jobAssignment: null,
                equipment: null,
            }));

            payload.hasAgreedToDriveCrew = selectedReview.hasAgreedToDriveCrew;

            if (this.shouldShowProjectCompletion()) {
                payload.wasProjectCompleted = selectedReview.wasProjectCompleted ?? false;
            }

            // Questions are for display purpose only; they should not be passed with update data.
            payload.questions = [];
            payload.earlyDepartureFlagCategoryId =
                showEarlyDeparture && earlyDeptSelection.leftEarly
                    ? earlyDeptSelection.categoryId
                    : null;

            // Remove timesheet in order to reduce payload size
            await httpClient.post(ApiRoutes.timesheetReview.update(), { ...payload, timeSheet: null });

            toasty.success(i18n.t('timesheet:saved'));
            this.open(timesheetId);
        } catch (err) {
            toasty.error(i18n.t('timesheet:employeeReviewSaveError', { error: err }));
        } finally {
            this.setState({ saving: false });
        }
    };

    onSigSavedCallback = (sigData) => {
        const { selectedReview } = { ...this.state };
        selectedReview.reviewSignature.signatureData = sigData;
        selectedReview.newEmployeeSignature = true;
        selectedReview.reviewSignature.sigIsComplete = true;
        this.setState({ selectedReview });
    };

    onSignatureTypeChanged = (signatureType) => {
        const { selectedReview } = { ...this.state };

        selectedReview.signatureTypeId = signatureType.value;

        if (selectedReview.signatureTypeId === TimesheetSignatureType.EmployeeNoSignature) {
            selectedReview.reviewSignature.signatureData = null;
            selectedReview.reviewSignature.sigIsComplete = false;
        }

        this.setState({ selectedReview });
    };

    setRef = (ref) => {
        // keep a reference to the dom ref as an instance property
        this.ref = ref;
        // give the dom ref to react-beautiful-dnd
        // this.props.innerRef(ref);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    open = async (timesheetId) => {
        this.disconnectWorkCardSignalR();
        this.setState(this.baseState);
        this.setState({ loading: true });

        let reviews = null;

        this.resetForm();
        await this.populateState();

        if (timesheetId) {
            reviews = await httpClient.js(ApiRoutes.timesheetReview.employeesByTimesheetId(timesheetId));
        } else {
            reviews = null;//
        }

        // if none then close i guess as its likely complete?
        if (!reviews.length) {
            this.onClose();
            return;
        }

        this.props.toggleShow(true);
        this.context.setFormOpened(true);

        const review = reviews.find((x) => x.employeeId == reviews[0].employeeId);

        // Signature images are no longer eagerly fetched for every review (see getReviewSignatureImage),
        // so reviewSignature.signatureData isn't a reliable "has a signature" signal here — use isComplete instead.
        const displayReuse = this.context?.user.isImpersonating && !!review.reviewSignature && !!review.isComplete;
        // Remove "No Signature" option if this is the crewlead
        let { signatureTypes } = { ...this.state };

        const employeeReview = reviews.find((x) => x.employeeId == this.context?.user?.employeeId);
        const isCrewLead = employeeReview?.isCrewLead;
        if (isCrewLead) {
            signatureTypes = signatureTypes.filter((x) => x.label !== 'No Signature');
        }
        this.setState({
            reviews,
            loading: false,
            timesheetId,
            employees: reviews.map((x) => ({ value: x.employeeId, label: x.employeeFullName })),
            selectedReview: review,
            selectedEmployeeId: reviews[0].employeeId,
            signatureTypes,
            displayReuse,
        });

        const validating = Boolean(this.props.tenantSettings?.enableWorkCardValidation);
        if (validating && timesheetId) {
            this.setupWorkCardSignalR(timesheetId);
        }

        if (this.shouldShowWorkCard()) {
            await this.getWorkCard(timesheetId, reviews[0].employeeId);
        }

        if (review.isComplete || review.reviewSignature?.sigIsComplete) {
            await this.getReviewSignatureImage(review.id);
        }
    };

    handleChangeNote = (timesheetDetailId, fieldName, value) => {
        const { selectedReview } = this.state;

        selectedReview.reviewTable = selectedReview.reviewTable.map((tc) => {
            if (tc.timesheetDetailId !== timesheetDetailId) {
                return tc;
            }

            return {
                ...tc,
                [fieldName]: value,
            };
        });

        selectedReview.requiredDetailNotes = selectedReview.requiredDetailNotes.map((tc) => {
            if (tc.timesheetDetailId !== timesheetDetailId) {
                return tc;
            }

            return {
                ...tc,
                [fieldName]: value,
            };
        });

        selectedReview.timeSheet.timesheetDetails = selectedReview.timeSheet.timesheetDetails.map((tsd) => {
            if (tsd.id !== timesheetDetailId) {
                return tsd;
            }

            return {
                ...tsd,
                [fieldName]: value,
            };
        });

        this.setState({ selectedReview });
    };

    async populateState() {
        const [signatureTypes, missingSigReasons, jobTypes] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.crewSignatureTypes()),
            httpClient.js(ApiRoutes.timesheetReview.missingSignatureReasons()),
            httpClient.js(ApiRoutes.typeAheads.jobTypes()),
        ]);

        this.setState({
            signatureTypes,
            missingSignatureReasons: missingSigReasons,
            jobTypesOptions: [...jobTypes],
        });
    }

    resetForm = () => this.setState({ formValidated: false });

    handleFileSelect = (files) => {
        this.setState({ pendingFiles: files });
    };

    onJobTypeChanged = onReactSelectChanged;

    onEarlyDepartureLeftEarlyChanged = (option) => {
        const { selectedEmployeeId, earlyDepartureSelections } = this.state;
        this.setState({
            earlyDepartureSelections: {
                ...earlyDepartureSelections,
                [selectedEmployeeId]: {
                    leftEarly: option.value,
                    categoryId: option.value ? earlyDepartureSelections[selectedEmployeeId]?.categoryId ?? null : null,
                },
            },
        });
    };

    onEarlyDepartureCategoryChanged = (option) => {
        const { selectedEmployeeId, earlyDepartureSelections } = this.state;
        this.setState({
            earlyDepartureSelections: {
                ...earlyDepartureSelections,
                [selectedEmployeeId]: {
                    ...earlyDepartureSelections[selectedEmployeeId],
                    categoryId: option.value,
                },
            },
        });
    };

    // #endregion
    render() {
        const {
            reviews,
            employees,
            selectedReview,
            selectedEmployeeId,
            signatureTypes,
            formValidated,
            validationMessage,
            missingSignatureReasons,
            displayReuse,
            jobTypesOptions,
            earlyDepartureSelections,
            earlyDepartureLeftEarlyOptions,
            earlyDepartureCategoryOptions,
        } = { ...this.state };

        const {
            tenantSettings,
        } = { ...this.props };

        if (!reviews) return null;

        // Get the parent question
        const parentQuestions = selectedReview?.questions?.filter((q) => q.parentQuestionId == null) || [];
        const parentQuestion = parentQuestions.find((parent) =>
            selectedReview.questions.some((child) => child.parentQuestionId === parent.id),
        );
        const parentQuestionId = parentQuestion?.id;

        // Check if "YES" is selected for the parent question
        const isYesSelected = selectedReview.answers?.some((answer) =>
            answer.questionId === parentQuestionId &&
            answer.questionOptionChoiceText?.toUpperCase().includes('YES'),
        );

        const showEarlyDepartureFields = tenantSettings.exemptLateArrivalsFromBumpRules && selectedReview?.isUnionWork;
        const earlyDeptSelection = earlyDepartureSelections[selectedEmployeeId] ?? { leftEarly: false, categoryId: null };

        const supportedFiles = SupportedFileExtensions.image;

        return (
            <SlideForm
                size="col-xl-6 col-lg-6 col-md-12 col-sm-12"
                loading={this.state.loading}
                show={this.props.show}
                id="employeeReview"
                formIcon={faUserClock}
                formTitle={i18n.t('timesheet:crewReviewSignatureTitle')}
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onClose={this.onClose}
                onDelete={this.onDelete}
                errors={this.state.errors}
                onClearErrors={this.onClearErrors}
                error={validationMessage}
                onSubmit={this.onSave}
                hideToolbar
            >
                <div>
                    <ValidatedSelect
                        id="selectedEmployeeId"
                        name="selectedEmployeeId"
                        label={i18n.t('timesheet:employeeLabel')}
                        options={employees}
                        value={(employees ?? []).find((s) => s.value === selectedEmployeeId) ?? ''}
                        onChange={this.onEmployeeSelected}
                    />

                    {!isEmpty(selectedReview) && (
                        <>
                            <FormGroup>
                                <SignatureReviewTable
                                    timeCharges={(selectedReview ?? {}).reviewTable}
                                    requiredDetailNotes={(selectedReview ?? {}).requiredDetailNotes}
                                    onChangeNote={this.handleChangeNote}
                                />
                            </FormGroup>
                            {
                                !!(selectedReview ?? {}).workOrderNumber
                                && (
                                    <FormGroup>
                                        <FormLabel label={i18n.t('timesheet:workOrder')} />
                                        <span className="ml-3 text-danger font-weight-bold">{i18n.t('timesheet:workOrderBracketed', { number: selectedReview.workOrderNumber })}</span>
                                    </FormGroup>
                                )
                            }
                            {
                                !!(selectedReview?.chargeTypeQuestionGroups ?? []).length
                                && selectedReview.chargeTypeQuestionGroups.map((qg, qgInx) => (
                                    <Card key={qgInx}>
                                        <CardBody>
                                            <CardTitle>{qg.chargeTypeName}</CardTitle>
                                            {
                                                qg.chargeTypeQuestions.map((q, qInx) => (
                                                    <SurveyQuestion
                                                        disabled={!!this.props.disabled}
                                                        key={qInx}
                                                        question={q}
                                                        questionIndex={qInx}
                                                        answers={selectedReview.chargeTypeAnswers}
                                                        onTextAnswerChanged={(event) => this.onReviewTextAnswerChanged(event, q, 'selectedReview', 'chargeTypeAnswers')}
                                                        onSelectAnswerChanged={(selection) => this.onReviewSelectAnswerChanged(selection, q, 'selectedReview', 'chargeTypeAnswers')}
                                                        onSelectChanged={(selection) => this.onReviewSelectChanged(selection, q, 'selectedReview', 'chargeTypeAnswers')}
                                                    />
                                                ))
                                            }
                                        </CardBody>
                                    </Card>
                                ))
                            }

                            {
                                !!(selectedReview?.questions ?? []).length
                                && selectedReview.questions
                                    .filter((q) => q.parentQuestionId == null)
                                    .map((q, qInx) => (
                                        <SurveyQuestion
                                            disabled={!!this.props.disabled}
                                            key={qInx}
                                            question={q}
                                            questionIndex={qInx}
                                            answers={selectedReview.answers}
                                            onTextAnswerChanged={(event) => this.onReviewTextAnswerChanged(q, event, 'selectedReview', 'answers')}
                                            onSelectAnswerChanged={(selection) => this.onReviewSelectAnswerChanged(selection, q, 'selectedReview', 'answers')}
                                            onSelectChanged={(selection) => this.onReviewSelectChanged(selection, q, 'selectedReview', 'answers')}
                                        />
                                    ))


                            }
                            {isYesSelected && selectedReview.questions
                                .filter((q) => q.parentQuestionId === parentQuestionId)
                                .map((q, qInx) => (
                                    q.questionOptions?.map((option, optionInx) => (
                                        <SurveyQuestion
                                            disabled={!!this.props.disabled}
                                            key={`${qInx}-${optionInx}`}
                                            question={{
                                                ...q,
                                                name: option.choiceText,
                                                inputTypeId: q.inputTypeId,
                                                questionOptions: [option],
                                            }}
                                            questionIndex={qInx}
                                            answers={selectedReview.answers}
                                            onTextAnswerChanged={(event) => this.onReviewTextAnswerChanged(event, { ...q, questionOptions: [option] }, 'selectedReview', 'answers')}
                                            onSelectAnswerChanged={(selection) => this.onReviewSelectAnswerChanged(selection, q, 'selectedReview', 'answers')}
                                            onSelectChanged={(selection) => this.onReviewSelectChanged(selection, q, 'selectedReview', 'answers')}
                                        />
                                    ))
                                ))
                            }

                            {!!(selectedReview ?? {}).timeSheet.notes && (
                                <Col size="12">
                                    <Textarea
                                        label={i18n.t('timesheet:notes')}
                                        readOnly
                                        id="timesheetNotes"
                                        rows="3"
                                        name="selectedReview.timeSheet.notes"
                                        className="form-control-sm p-2"
                                        value={selectedReview.timeSheet.notes ?? ''}
                                    />
                                </Col>
                            )
                            }
                            {!isEmpty(selectedReview) && Boolean(showEarlyDepartureFields) && (
                                <ValidatedSelect
                                    id="earlyDepartureLeftEarly"
                                    name="earlyDepartureLeftEarly"
                                    label={i18n.t('timesheet:earlyDepartureLeftEarlyLabel')}
                                    options={earlyDepartureLeftEarlyOptions}
                                    value={earlyDepartureLeftEarlyOptions.find((o) => o.value === earlyDeptSelection.leftEarly) ?? earlyDepartureLeftEarlyOptions[0]}
                                    onChange={this.onEarlyDepartureLeftEarlyChanged}
                                />
                            )}
                            {!isEmpty(selectedReview) && Boolean(showEarlyDepartureFields && earlyDeptSelection.leftEarly) && (
                                <ValidatedSelect
                                    id="earlyDepartureCategoryId"
                                    name="earlyDepartureCategoryId"
                                    label={i18n.t('timesheet:earlyDepartureDurationLabel')}
                                    options={earlyDepartureCategoryOptions}
                                    value={earlyDepartureCategoryOptions.find((o) => o.value === earlyDeptSelection.categoryId) ?? null}
                                    onChange={this.onEarlyDepartureCategoryChanged}
                                    required
                                />
                            )}
                            {selectedReview.isCrewLead && selectedReview?.jobTypeId !== JobTypes.Historic && (
                                <ValidatedSelect
                                    id="jobType"
                                    name="selectedReview.jobTypeId"
                                    label={i18n.t('timesheet:workTypeQuestion')}
                                    options={jobTypesOptions}
                                    value={
                                        (jobTypesOptions ?? []).find(
                                            (x) => x.value
                                                === selectedReview.jobTypeId,
                                        ) ?? ''
                                    }
                                    onChange={this.onJobTypeChanged}
                                    required
                                    error={i18n.t('timesheet:jobTypeSelectionRequired')}
                                />
                            )}

                            {displayReuse && (
                                <FormCheckbox
                                    labelClass="text-danger text-small"
                                    className="mt-2 pl-2 mb-3"
                                    id="reuseEmployeeSignature"
                                    name="reuseEmployeeSignature"
                                    disabled={!!selectedReview.isComplete}
                                    checked={selectedReview.reuseEmployeeSignature}
                                    onChange={this.onReuseSignatureChanged}
                                    labelText={i18n.t('timesheet:reuseSignatureLabel')}
                                />
                            )}

                            {tenantSettings.enableDriverRideConfirmationFields && this.isCrewDriver() && (
                                <>
                                    {!this.hasAgreedToDriverDisclaimer() && (
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="hasAgreedToDriverDisclaimer"
                                                label={i18n.t('confirm:driverDisclaimerLabel')}
                                                required
                                            />

                                            <FormCheckbox
                                                id="hasAgreedToDriverDisclaimer"
                                                name="selectedReview.hasAgreedToDriverDisclaimer"
                                                onChange={(e) => {
                                                    const { selectedReview } = this.state;
                                                    selectedReview.hasAgreedToDriverDisclaimer = e.target.checked;

                                                    this.setState({
                                                        selectedReview,
                                                        hasAgreedToDriverDisclaimerError: !e.target.checked,
                                                    });
                                                }}
                                                checked={selectedReview?.hasAgreedToDriverDisclaimer || false}
                                                labelText={i18n.t('confirm:driverDisclaimerText')}
                                            />

                                            {this.state.hasAgreedToDriverDisclaimerError && (
                                                <small className="text-danger">{i18n.t('confirm:driverDisclaimerMustAgree')}</small>
                                            )}
                                        </FormGroup>
                                    )}

                                    <FormGroup>
                                        <FormLabel
                                            htmlFor="hasAgreedToDriveCrew"
                                            label={i18n.t('timesheet:driverAgreement')}
                                        />
                                        <FormCheckbox
                                            id="hasAgreedToDriveCrew"
                                            name="selectedReview.hasAgreedToDriveCrew"
                                            onChange={(e) => {
                                                const { selectedReview } = this.state;
                                                selectedReview.hasAgreedToDriveCrew = e.target.checked;
                                                this.setState({ selectedReview });
                                            }}
                                            checked={selectedReview?.hasAgreedToDriveCrew || false}
                                            labelText={i18n.t('timesheet:driverAgreementText')}
                                        />

                                        {selectedReview?.hasAgreedToDriveCrew && (
                                            <small className="text-muted">
                                                {i18n.t('timesheet:driverNonFlaggingNotice')}
                                            </small>
                                        )}
                                    </FormGroup>
                                </>
                            )}

                            {this.shouldShowProjectCompletion() && (
                                <FormGroup>
                                    <YesNoButtons
                                        id="wasProjectCompleted"
                                        label={i18n.t('timesheet:wasProjectCompletedLabel')}
                                        name="selectedReview.wasProjectCompleted"
                                        value={selectedReview.wasProjectCompleted ?? false}
                                        onClick={(value) => {
                                            const { selectedReview } = this.state;
                                            selectedReview.wasProjectCompleted = value;
                                            this.setState({ selectedReview });
                                        }}
                                    />
                                </FormGroup>
                            )}

                            {!(selectedReview.reuseEmployeeSignature) && (
                                <ValidatedSelect
                                    id="empSignatureTypeId"
                                    name="selectedReview.signatureTypeId"
                                    label={i18n.t('timesheet:signatureType')}
                                    required
                                    options={signatureTypes}
                                    value={(signatureTypes ?? []).find((s) => s.value === selectedReview.signatureTypeId) ?? ''}
                                    onChange={this.onSignatureTypeChanged}
                                    error={i18n.t('timesheet:signatureTypeRequired')}
                                    hideClear
                                />
                            )}

                            {
                                (selectedReview ?? {}).signatureTypeId === TimesheetSignatureType.EmployeeNoSignature
                                && (
                                    <NoReasonInput
                                        tenantSettings={tenantSettings}
                                        review={selectedReview}
                                        formValidated={formValidated}
                                        missingSignatureReasons={missingSignatureReasons}
                                        onMissingSigReasonChanged={this.onMissingSigReasonChanged}
                                        onChangeNotes={this.onChangeNotes}
                                    />
                                )
                            }
                            {
                                (selectedReview ?? {}).signatureTypeId !== TimesheetSignatureType.EmployeeNoSignature && !(selectedReview.reuseEmployeeSignature)
                                && (
                                    <>
                                        {
                                            (!!selectedReview.isComplete || !!selectedReview.reviewSignature.sigIsComplete)
                                            && (
                                                <FormGroup>
                                                    <FormLabel
                                                        htmlFor="empSigPreview"
                                                        label={i18n.t('timesheet:employeeSignatureLabel')}
                                                    />
                                                    <SignatureImage
                                                        alt={i18n.t('timesheet:signaturePreview')}
                                                        id="empSigPreview"
                                                        style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ccc' }}
                                                        src={selectedReview.reviewSignature.signatureData}
                                                    />
                                                </FormGroup>
                                            )
                                        }
                                        {
                                            (!selectedReview.isComplete && !selectedReview.reviewSignature.sigIsComplete)
                                            && (
                                                <SignatureButton
                                                    id="employeeReviewButton"
                                                    onSigSavedCallback={this.onSigSavedCallback}
                                                    sigData={selectedReview?.reuseEmployeeSignature ? selectedReview?.reviewSignature?.signatureData : null}
                                                    reviewId={selectedReview?.id}
                                                />
                                            )
                                        }

                                    </>
                                )
                            }
                            {this.shouldShowWorkCard() && (
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="workCardAttachments"
                                        label={i18n.t('timesheet:uploadWorkCard')}
                                        required={this.isWorkCardRequired()}
                                    />
                                    <FlexRow>
                                        <FlexColumnCenter className="col-sm-6 col-md-5">
                                            <FileUpload
                                                id="jobUploader"
                                                accept={supportedFiles}
                                                maxNameLength={150}
                                                onQueueStarted={this.handleUploadStart}
                                                onUploadComplete={this.handleUploadComplete}
                                                url={ApiRoutes.timesheetReview.postWorkCard(
                                                    selectedReview.timesheetId,
                                                    selectedEmployeeId,
                                                )}
                                            />
                                        </FlexColumnCenter>
                                        {selectedReview.workCard && (
                                            <FlexColumnCenter className="col-sm-6 col-md-5">
                                                <img
                                                    src={selectedReview.workCard || ''}
                                                    alt={i18n.t('timesheet:workCardAlt')}
                                                    className="work-card-image"
                                                />
                                            </FlexColumnCenter>
                                        )}
                                    </FlexRow>
                                    {tenantSettings.enableWorkCardValidation && (
                                        <WorkCardValidationStatus
                                            status={this.state.workCardValidationStatus}
                                            uploading={this.state.workCardUploading}
                                            onRetry={() => {
                                                this.setState({ workCardValidationStatus: null });
                                                document.getElementById('jobUploader')?.click();
                                            }}
                                        />
                                    )}
                                    {this.isWorkCardRequired() && !selectedReview.workCard && !this.state.workCardValidationStatus && (
                                        <small className="text-danger">
                                            {i18n.t('timesheet:workCardRequiredForTimesheet')}
                                        </small>
                                    )}
                                </FormGroup>
                            )}
                        </>
                    )}
                </div>

                <FlexCenterRow className="mt-2">
                    <SaveButton
                        onClick={this.onSave}
                        saving={this.state.saving}
                        disabled={this.state.saving || this.isWorkCardValidating()}
                    />
                </FlexCenterRow>
            </SlideForm>
        );
    }
}
